/**
 * The Dynamics board's visual grammar, in one place so the KPI strip, the
 * charts, the "worst N" grids and the L2 pages can never disagree.
 *
 * Two things live here that no other board needs:
 *
 * 1. **`access_state` copy.** The backend distinguishes five ways this board can
 *    have no data (`no_credential`, `unauthorised`, `workspace_not_found`,
 *    `query_rejected`, `unavailable`) because a 403 and a 404 are different
 *    tickets. Each one gets its own heading, tone, icon and remedy list — the
 *    text an operator needs is what the *state* means, on top of the backend's
 *    own `status_detail`, which is always rendered verbatim beside it.
 * 2. **A hard rule against zero.** `formatMs` / `formatCount` / `formatPercent`
 *    all render `null` as an em dash. Nothing in this module ever coerces an
 *    absent measurement to `0`: the "Other" cohort row genuinely has no P95
 *    (a percentile of percentiles is not a percentile), an environment that ran
 *    no synthetic tests has no availability, and a workspace that 404s measured
 *    nothing at all. Zero is a measurement; these are not.
 */

import {
  Ban,
  CircleCheck,
  CircleEllipsis,
  CloudCog,
  CloudOff,
  type IconComponent,
  OctagonAlert,
  SearchX,
  ShieldOff,
  TriangleAlert,
} from "@norican/icons";

import type { FoCohortSuppression, FoEnvironmentStatus, FoOverview, FoResultCode } from "@/lib/api";
import {
  formatCount as formatCountShared,
  formatPercent as formatPercentShared,
  type StatusTone,
} from "@/lib/charts/config";

/** The one "nothing to show" glyph on this board, exported so tiles reuse it. */
export const EM_DASH = "—";

/* ------------------------------------------------------------------------ *
 * Access state — the board's primary state, not an afterthought
 * ------------------------------------------------------------------------ */

/** The panel access states, taken from the generated payload — never re-typed. */
export type FoAccessState = FoOverview["access_state"];

/** Health verdict shared by environments and Service Bus queues. */
export type FoHealthState = FoEnvironmentStatus["state"];

export interface AccessStateCopy {
  /** Panel heading — names the *kind* of problem, not the symptom. */
  title: string;
  /** Card accent / `Alert` tone. */
  tone: "success" | "warning" | "danger" | "neutral";
  /** Icon paired with the tone — state is never colour-alone (§3.2). */
  icon: IconComponent;
  /** Whose problem this is, in one clause. */
  owner: string;
  /** What to do about it, most direct fix first. */
  remedy: readonly string[];
  /** Short label for a `Badge` when the state is mentioned inline. */
  badge: string;
}

/**
 * What each `access_state` means and what to do about it.
 *
 * Six entries, one per `FoAccessState` — `ok` plus the five different ways this
 * board can have no data — rather than one "unavailable" catch-all. The record
 * is typed `Record<FoAccessState, …>`, so the compiler is what keeps it
 * exhaustive; a sixth failure state added in the backend fails the build here.
 * `unauthorised` sends someone to an Owner of the subscription to grant a role;
 * `workspace_not_found` sends someone to the configuration to fix an address.
 * Collapsing them would send every operator to the wrong place half the time.
 */
export const ACCESS_COPY: Record<FoAccessState, AccessStateCopy> = {
  ok: {
    title: "Reading live telemetry",
    tone: "success",
    icon: CircleCheck,
    owner: "Nothing to do.",
    remedy: [],
    badge: "Live",
  },
  no_credential: {
    title: "NOVA has no Azure credential",
    tone: "warning",
    icon: CloudCog,
    owner: "A NOVA deployment gap — nothing was asked of Azure at all.",
    remedy: [
      "In Azure — assign a managed identity to the nova-api Container App, then grant it the workspace role below.",
      "Anywhere — set AZURE_TENANT_ID, AZURE_CLIENT_ID and AZURE_CLIENT_SECRET for a service principal.",
      "An az login on the host is deliberately not used: NOVA never authenticates as a person.",
    ],
    badge: "No credential",
  },
  unauthorised: {
    title: "NOVA's identity is not authorised for this workspace",
    tone: "warning",
    icon: ShieldOff,
    owner:
      "A missing role assignment. The workspace exists and answered — it refused the caller, not the address.",
    remedy: [
      "Grant NOVA's identity the Log Analytics Reader role at the workspace scope named below.",
      "An Owner of the workspace's subscription can do it; no code change or redeploy is needed.",
      "This is not a wrong resource ID — the workspace resolved. Correcting configuration would not help.",
    ],
    badge: "403 unauthorised",
  },
  workspace_not_found: {
    title: "This workspace does not exist at the configured resource ID",
    tone: "danger",
    icon: SearchX,
    owner:
      "A configuration fault. Log Analytics answered 404: there is nothing at the address NOVA was told to read.",
    remedy: [
      "Correct the workspace name, resource group or subscription in NOVA's configuration.",
      "The resource ID that was tried is shown below verbatim, so it can be compared against the real workspace.",
      "Granting a role would not help — a role cannot be assigned on a resource that is not there.",
    ],
    badge: "404 not found",
  },
  query_rejected: {
    title: "Log Analytics rejected the query",
    tone: "danger",
    icon: Ban,
    owner: "A NOVA-side fault: the credential and the workspace are both fine, the query is not.",
    remedy: [
      "Usually schema drift — a table or column the query names has changed shape upstream.",
      "Sometimes a reserved word used as a bare identifier; the backend keeps a list of those.",
      "Nothing an operator can configure. This one is a code fix.",
    ],
    badge: "Query rejected",
  },
  unavailable: {
    title: "Log Analytics did not answer",
    tone: "warning",
    icon: CloudOff,
    owner: "Upstream: the service was reachable but the request did not complete.",
    remedy: [
      "Transient by nature — the next poll may well succeed, and the board recovers on its own.",
      "If it persists, check Azure Monitor's own service health for the region.",
    ],
    badge: "Unavailable",
  },
};

/**
 * The single sentence every non-`ok` panel carries.
 *
 * The whole point of this board's state handling: a panel with no data shows
 * *nothing*, and says why, rather than showing `0`. A 404 rendered as a zero
 * backlog or a flat zero line would read as a healthy, quiet system — exactly
 * the flattering direction the design rules warn about (§5, §8).
 */
export const NOT_ZERO_NOTE =
  "No measurement was taken, so this panel is blank rather than zero. Nothing here is simulated, " +
  "estimated, or carried over from a previous read.";

/* ------------------------------------------------------------------------ *
 * Health verdicts (environments, queues) — a status scale
 * ------------------------------------------------------------------------ */

/**
 * What each probe state is called on screen.
 *
 * "Answered", not "Healthy". This state is derived from a synthetic URL ping
 * that follows redirects and never signs in, plus a throttling count — so it
 * says the front door replied, and nothing about the application tier, the
 * database, or how long a form took. Calling that "Healthy" is precisely the
 * misreading the availability section's own header warns against, and it was
 * being printed inside that section.
 */
export const HEALTH_LABEL: Record<FoHealthState, string> = {
  healthy: "Answered",
  degraded: "Degraded",
  failing: "Failing",
  unknown: "Unknown",
};

/**
 * Tones for those states. `healthy` is deliberately NOT `success`.
 *
 * Green is a verdict about the system, and this measurement cannot support one.
 * Neutral says "the probe replied" without dressing it as health; degraded and
 * failing keep their status tones, because a probe that did NOT reply is a real
 * finding and the one thing this signal can state on its own.
 */
export const HEALTH_TONE: Record<FoHealthState, StatusTone> = {
  healthy: "neutral",
  degraded: "warning",
  failing: "danger",
  unknown: "neutral",
};

/** Chart fills — DS status tokens, never raw hex (§3.1). */
export const HEALTH_COLOR: Record<FoHealthState, string> = {
  // Matches HEALTH_TONE: an answering probe is drawn neutral, not green, for the
  // same reason it is not called "Healthy". The two must not drift — a chart
  // that paints success under a legend that says "Answered" reinstates the claim
  // the label just gave up.
  healthy: "var(--norican-fg-muted)",
  degraded: "var(--norican-warning)",
  failing: "var(--norican-danger)",
  unknown: "var(--norican-bg-muted)",
};

/** Icon paired with each verdict, so status is never colour-alone (§3.2). */
export const HEALTH_ICON: Record<FoHealthState, IconComponent> = {
  healthy: CircleCheck,
  degraded: TriangleAlert,
  failing: OctagonAlert,
  unknown: CircleEllipsis,
};

/** Order used wherever verdicts are listed or legended — problems first. */
export const HEALTH_ORDER: readonly FoHealthState[] = ["failing", "degraded", "unknown", "healthy"];

/* ------------------------------------------------------------------------ *
 * Result codes — also a status scale, not a categorical one
 * ------------------------------------------------------------------------ */

export interface ResultCodeStatus {
  label: string;
  tone: StatusTone;
  color: string;
  icon: IconComponent;
}

/**
 * An F&O result code's status semantics.
 *
 * A result code is a verdict, not an identity: `500` is broken and `200` is
 * good, so these wear the reserved status tones rather than the categorical
 * ramp (§3.2). Parsed as a string with an explicit fallback, because F&O
 * returns codes the code has never seen (§8 — no enum that mirrors live data).
 */
export function resultCodeStatus(code: string): ResultCodeStatus {
  const leading = code.trim().charAt(0);
  if (leading === "2") {
    return {
      label: `${code} succeeded`,
      tone: "success",
      color: HEALTH_COLOR.healthy,
      icon: CircleCheck,
    };
  }
  if (leading === "3") {
    return {
      label: `${code} redirected`,
      tone: "neutral",
      color: "var(--norican-bg-muted)",
      icon: CircleEllipsis,
    };
  }
  if (leading === "4") {
    return {
      label: `${code} client error`,
      tone: "warning",
      color: HEALTH_COLOR.degraded,
      icon: TriangleAlert,
    };
  }
  if (leading === "5") {
    return {
      label: `${code} server error`,
      tone: "danger",
      color: HEALTH_COLOR.failing,
      icon: OctagonAlert,
    };
  }
  return {
    label: code === "" ? "Unlabelled" : code,
    tone: "neutral",
    color: "var(--norican-bg-muted)",
    icon: CircleEllipsis,
  };
}

/** Requests carrying a result code that F&O reports as a failure verdict. */
export function isFailureCode(row: FoResultCode): boolean {
  return row.failed > 0 || /^[45]/.test(row.result_code.trim());
}

/* ------------------------------------------------------------------------ *
 * Formatters — every one of them renders `null` as an em dash
 * ------------------------------------------------------------------------ */

/** Thousands-separated integer, or an em dash. Never `0` for "unknown". */
export function formatCount(value: number | null | undefined): string {
  return formatCountShared(value);
}

/**
 * A whole-number count from a metric that arrives as a float, or an em dash.
 *
 * Service Bus reports `active_messages` and `dead_lettered_messages` as means
 * over a 5-minute bin, so they are floats — `formatCount` would print
 * "1,234.567 messages", which claims a precision a message queue does not have.
 * Rounding happens here rather than in the shared `formatCount` because other
 * boards format genuinely fractional values with it.
 */
export function formatMessageCount(value: number | null | undefined): string {
  return formatCountShared(value == null ? value : Math.round(value));
}

/**
 * Coerce a Recharts tooltip value to a number, or `null` when there is none.
 *
 * `Chart.TooltipContent` types its `formatter` as
 * `(value: number | string | undefined) => ReactNode` and calls it
 * unconditionally, so a series with no value at the hovered X arrives as
 * `undefined`. `Number(undefined)` is `NaN` and `Number(null)` is `0` — the
 * first renders "NaN" and the second renders a fabricated zero, which is the
 * worse of the two. This funnels both into `null` so the null-safe formatters
 * do what they were written to do: render the gap as a gap (§5).
 */
export function tooltipValue(value: number | string | undefined): number | null {
  if (value == null || value === "") {
    return null;
  }
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** A 0–100 value as a percentage with one decimal, or an em dash. */
export function formatPercent(value: number | null | undefined): string {
  return formatPercentShared(value);
}

/**
 * A duration in the unit a human reads it in — `420 ms`, `6.2 s`, `41 s`.
 *
 * Returns an em dash for `null`, which is load-bearing: the folded "Other"
 * cohort row has no `p95_ms` at all, and printing `0 ms` there would claim the
 * slowest unlabelled forms are instant.
 */
export function formatMs(value: number | null | undefined): string {
  if (value == null) {
    return EM_DASH;
  }
  if (value < 1000) {
    return `${Math.round(value)} ms`;
  }
  const seconds = value / 1000;
  return `${seconds.toFixed(seconds >= 10 ? 0 : 1)} s`;
}

/** Compact duration for axis ticks — `0.5s`, `6s`, `41s`. */
export function formatMsCompact(value: number): string {
  if (value < 1000) {
    return `${Math.round(value)}ms`;
  }
  const seconds = value / 1000;
  return `${seconds.toFixed(seconds >= 10 ? 0 : 1)}s`;
}

/**
 * An hour-of-day bucket, labelled `13:00 UTC`.
 *
 * UTC is printed rather than converted on purpose: `hour_of_day` is a clock
 * bucket aggregated across many days, so it cannot be shifted into a local
 * zone without inventing a mapping. Reading a Copenhagen working day off an
 * unlabelled UTC axis is exactly the quiet error the backend model warns about.
 */
export function formatHourUtc(hour: number): string {
  return `${`${hour}`.padStart(2, "0")}:00`;
}

/** A 5-minute or hourly bin as a local wall-clock time, or an em dash. */
export function formatBinTime(value: string | null | undefined): string {
  const parsed = value == null ? null : new Date(value);
  if (parsed == null || Number.isNaN(parsed.getTime())) {
    return EM_DASH;
  }
  return parsed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/** A bin spanning more than a day — `20 Aug 14:00`. */
export function formatBinDateTime(value: string | null | undefined): string {
  const parsed = value == null ? null : new Date(value);
  if (parsed == null || Number.isNaN(parsed.getTime())) {
    return EM_DASH;
  }
  return parsed.toLocaleString([], {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const WINDOW_PATTERN = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?$/;

/**
 * An ISO 8601 duration as a caption — `PT24H` → `last 24 hours`.
 *
 * Every panel states its own window because they genuinely differ (overview
 * 24 h, user experience 7 days, integration 6 h), and none of them is a filter
 * the user can change: the backend's windows are fixed. That is why this board
 * has no `DateRangePicker` — a control that changed nothing would be a lie.
 */
export function formatWindow(window: string): string {
  const match = WINDOW_PATTERN.exec(window);
  if (!match) {
    return window;
  }
  const units: [string | undefined, string][] = [
    [match[1], "day"],
    [match[2], "hour"],
    [match[3], "minute"],
  ];
  const parts = units
    .filter(([value]) => value != null)
    .map(([value, unit]) => `${value} ${unit}${value === "1" ? "" : "s"}`);
  return parts.length > 0 ? `last ${parts.join(" ")}` : window;
}

/**
 * The k-anonymity fold, stated rather than hidden.
 *
 * A board that silently drops rows lies by omission, so the fold is a visible
 * caption on every panel that ranks cohorts. `noun` names what was folded —
 * loads for forms, requests for request names.
 */
export function suppressionCaption(
  suppression: FoCohortSuppression,
  noun: "loads" | "requests",
): string {
  if (suppression.cohorts_folded === 0) {
    return `No cohort fell below the k≥${suppression.min_users} floor — every row below is labelled.`;
  }
  return (
    `${suppression.cohorts_folded} cohorts used by fewer than ${suppression.min_users} people were ` +
    `folded into one unlabelled "Other" row, carrying ${formatCount(suppression.events_folded)} ` +
    `${noun}. That row has no percentile: a percentile of percentiles is not a percentile.`
  );
}

/** A dependency's display name — type and target only, never a statement. */
export function dependencyLabel(dependencyType: string, target: string): string {
  return `${dependencyType} · ${target}`;
}
